import React, {
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
  useCallback,
  useState,
  useEffect,
} from "react";

import { Box } from "@mui/material";
import ShipeeziGrid, { Column, Category } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import {
  calculateBookingCartonTotals,
  calculatePurchaseOrderTotal,
} from "../../../../../../components/Booking/Maintenance/util";
import Button, { Color } from "../../../../../../components/Form/Button";
import BadgeBookingStatus from "../../../../../../components/Generic/BadgeBookingStatus";
import Modal from "../../../../../../components/Generic/Modal";
import { DefaultTooltip } from "../../../../../../components/Generic/Tooltip";
import { Booking } from "../../../../../../models/booking";
import { Company } from "../../../../../../models/company";
import { Filters, Searches } from "../../../../../../models/shipeezi.table";
import { Shipment } from "../../../../../../models/shipments";
import {
  capitalizeFirstLetter,
  capitalizeFirstLetterAllWorlds,
} from "../../../../../../util/capitalizeFirstLetter";
import {
  BookingModes,
  BookingStatus,
  ShipmentStatus,
  ShipmentType,
} from "../../../../../../util/enums";
import useAction from "../../hooks/useActions";
import useStyles from "./styles";

export interface BookingBasedHandles {
  open: () => void;
  close: () => void;
}
interface BookingBasedProps {
  onGetBooking: (shipment: Shipment) => void;
  onCancel?: () => void;
}

const BookingBasedModal: ForwardRefRenderFunction<BookingBasedHandles, BookingBasedProps> = (
  props,
  ref
) => {
  const { onGetBooking, onCancel } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [selecteds, setSelecteds] = useState<number[]>();
  const [modalOpened, setModalOpened] = useState(false);

  const [totalItems, setTotalItems] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [searches, setSearches] = useState<Searches>();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const defaultFilters = {
    status_shipment: [BookingStatus.RECEIVED],
  };
  const [searchFilters, setSearchFilters] = useState<Filters>(defaultFilters);

  const { getAvailableBookings, useGetBooking } = useAction();

  const { data, isLoading } = getAvailableBookings(page, pageSize, searches, searchFilters);

  const renderShipmentMode = (shipmentType: string) => {
    return <span>{capitalizeFirstLetter(shipmentType)}</span>;
  };
  const columns: Column[] = [
    {
      name: "containerId",
      title: `${translate("booking")} #`,
      searchIdentifier: "container_id",
    },
    {
      name: "shipmentType",
      title: translate("mode"),
      formatComponent: (value: string) => value && renderShipmentMode(value),
      hideSearch: true,
    },

    {
      name: "company",
      title: translate("supplier"),
      formatText: (company: Company) => company?.supplierAssociated?.name || "",
      searchIdentifier: "supplierAssociated.name",
      hideSearch: true,
    },
    {
      name: "totalQuantity",
      title: translate("cartonQuantity"),
      hideSearch: true,
    },

    {
      name: "origin",
      title: translate("origin"),
    },
    {
      name: "destination",
      title: translate("destination"),
    },
    {
      name: "statusShipment",
      title: translate("status"),
      formatComponent: (status: string) =>
        status && <BadgeBookingStatus value={capitalizeFirstLetterAllWorlds(status)} />,
      hideSearch: true,
    },
  ];

  useEffect(() => {
    if (data && data?.rows) {
      setBookings(data.rows);
      setTotalItems(data.count);
    }
  }, [data]);

  const filterCategories: Category[] = [
    {
      name: translate("mode"),
      column: "shipment_type",
      options: [
        { value: BookingModes.SEA, label: translate("sea") },
        { value: BookingModes.AIR, label: translate("air") },
        { value: BookingModes.ROAD, label: translate("road") },
      ],
    },
  ];

  const openModal = useCallback(() => {
    setModalOpened(true);
  }, []);

  const closeModal = useCallback(() => {
    setSelecteds([]);
    setModalOpened(false);
    setPage(0);
    setPageSize(10);
    setSearches([]);

    if (onCancel) onCancel();
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const onSave = (booking?: Shipment) => {
    if (booking) {
      delete booking?.milestones;
      const shipment = {
        ...booking,
        id: undefined,
        containerId: undefined,
        company: undefined,
        serviceType: undefined,
        bookingId: booking?.id,
        bookingNo: booking?.containerId,
        isBooking: false,
        shipperEntity: {
          supplierId: booking?.company?.supplierAssociated?.id,
          fullName: booking?.company?.supplierAssociated?.name,
          code: booking?.company?.supplierAssociated?.code,
        },
        statusShipment:
          booking?.shipmentType === ShipmentType.SEA
            ? ShipmentStatus.BOOKED
            : ShipmentStatus.PLANNED,
        ...calculateBookingCartonTotals(booking?.cartons || []),
        purchaseOrders: calculatePurchaseOrderTotal(
          booking?.purchaseOrders || [],
          booking?.cartons || []
        ),
        loadType: booking?.serviceType,
        containers: booking?.containers || [],
      } as Shipment;

      onGetBooking(shipment);
    }
  };
  const { mutate: getBooking, isLoading: loadingBooking } = useGetBooking(onSave);

  const onSubmit = () => {
    if (selecteds && selecteds?.[0]) getBooking(selecteds?.[0]);
  };

  const renderSubtitle = () => (
    <div className={classes.subtitle}>
      {translate("selectABookingBellowToStartAShipmentBasedOnIt")}.
    </div>
  );

  if (!modalOpened) return null;

  const handleSetPage = (page: number) => {
    setPage(page - 1);
  };

  const handleFilter = (value: Filters) => {
    setSearchFilters(value);
    setPage(0);
  };

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };
  const customButtons = (
    <>
      <Button
        variant="text"
        data-cy="button-cancel"
        onClick={closeModal}
        customColor={Color.CANCEL}
      >
        {translate("cancel")}
      </Button>

      <DefaultTooltip
        title={selecteds?.length === 1 ? "" : translate("selectJustOneBookingPerShipment")}
        placement="top"
      >
        <div>
          <Button
            data-cy="button-save"
            onClick={onSubmit}
            customColor={Color.SUCCESS}
            disabled={!selecteds?.length || selecteds?.length > 1}
            className={classes.rightButton}
            loading={selecteds?.length === 1 ? loadingBooking : false}
          >
            {`${translate("add")}`}
          </Button>
        </div>
      </DefaultTooltip>
    </>
  );

  return (
    <Modal
      open={modalOpened}
      handleClose={closeModal}
      showModalFooter
      showActionButtons
      actionButtonProps={{
        textSave: translate("add"),
        saveDisabled: !selecteds?.length,
        showCancelButton: false,
        showSaveButton: false,
        onSave,
        onCancel: closeModal,
      }}
      title={translate("selectABooking")}
      className={classes.modal}
      customRightButtons={customButtons}
    >
      <Box className={classes.container}>
        {renderSubtitle()}
        <ShipeeziGrid
          data={bookings}
          totalItems={totalItems}
          columns={columns}
          loadingData={isLoading}
          emptyData={translate("noInformationToBeShown")}
          selecteds={selecteds}
          handleSelect={setSelecteds}
          pageSize={pageSize}
          currentPage={page}
          pageSizeList={[10, 20, 30, 100]}
          onChangePageSize={setPageSize}
          onNextPage={handleSetPage}
          onPreviousPage={handleSetPage}
          onGoToPage={handleSetPage}
          generalActions={{
            orderBy: {
              left: ["handleSearch", "handleFilterBy"],
            },
            handleSearch: {
              onSearch: handleSearch,
            },
            handleFilterBy: {
              categories: filterCategories,
              onChange: handleFilter,
            },
          }}
        />
      </Box>
    </Modal>
  );
};

export default forwardRef(BookingBasedModal);
